import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Check, Minus } from 'lucide-react';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  width: '100%',
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 500,
  marginBottom: 8,
  lineHeight: 1.3,
  textAlign: 'center',
}));

const ButtonsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 16,
  marginTop: 32,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginTop: 24,
  },
}));

// Symmetric, no-valence styling: both Yes and No use the brand primary accent
// when selected. Avoids the green/red right-vs-wrong cue that a personality
// instrument absolutely shouldn't carry.
const BooleanButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  flex: 1,
  padding: '16px 24px',
  borderRadius: 16,
  border: isSelected
    ? `2px solid ${theme.palette.primary.main}`
    : '2px solid #e0e0e0',
  backgroundColor: '#fff',
  color: isSelected ? theme.palette.primary.main : theme.palette.grey[600],
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  transition: 'border-color 120ms ease, color 120ms ease',
  '&:hover': {
    backgroundColor: '#fff',
    border: isSelected
      ? `2px solid ${theme.palette.primary.main}`
      : '2px solid #bdbdbd',
  },
}));

export default function TrueFalse({
  question,
  selected,
  onSelect,
  yesLabel,
  noLabel,
  disabled = false,
}) {
  const { t } = useTranslation();
  const yes = yesLabel ?? t('Assessment.yesLabel');
  const no = noLabel ?? t('Assessment.noLabel');
  return (
    <Container>
      <QuestionText>{question}</QuestionText>
      <ButtonsContainer>
        <BooleanButton
          isSelected={selected === true}
          onClick={() => !disabled && onSelect(true)}
          disabled={disabled}
        >
          <Check size={20} strokeWidth={2.25} />
          {yes}
        </BooleanButton>
        <BooleanButton
          isSelected={selected === false}
          onClick={() => !disabled && onSelect(false)}
          disabled={disabled}
        >
          <Minus size={20} strokeWidth={2.25} />
          {no}
        </BooleanButton>
      </ButtonsContainer>
    </Container>
  );
}
